/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _6 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>6 - React и Redux</h2>
    <p className={styles.description}>
      Для того чтобы использовать React и Redux вместе, необходимо добавить пакет{' '}
      <code className={styles.code}>react-redux</code>. Это набор компонентов связывающих React-компоненты и
      Redux-хранилище через контекст Context.{' '}
    </p>
    <CopyBlock text={`npm install react-redux`} language="javascript" showLineNumbers={false} theme={ocean} />
    <p className={styles.description}>
      <a className={styles.link} target="_blank" rel="noopener noreferrer" href="https://react-redux.js.org/">
        Документация react-redux{' '}
      </a>
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>6.1 - Provider</h3>
      <p className={styles.description}>
        Компонент <code className={styles.code}>{`<Provider>`}</code>, оборачивает все дерево компонентов приложения и,
        используя контекст, предоставляет <code className={styles.code}>store</code> и его методы.{' '}
      </p>
      <CopyBlock
        text={`import { createStore } from 'redux';
import { Provider } from 'react-redux';

// Болванка под reducer
const reducer = (state = {}, action) => state;

const store = createStore(reducer);

<Provider store={store}>
  <App />
</Provider>;
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://react-redux.js.org/api/provider"
        >
          Документация Provider{' '}
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>6.2 - connect()</h3>
      <p className={styles.description}>
        Если какой-либо компонент хочет получить доступ к <code className={styles.code}>store</code>, он должен быть
        обернут в функцию <code className={styles.code}>connect()</code>, которая свяжет компонент и{' '}
        <code className={styles.code}>store</code>. Предоставляет доступ к <code className={styles.code}>state</code> и{' '}
        <code className={styles.code}>disaptch()</code>.{' '}
      </p>
      <p className={styles.description}>
        <code className={styles.code}>connect</code> это HOC, он не изменяет переданный ему компонент, а возвращает
        новый компонент связанный с хранилищем.{' '}
      </p>
      <CopyBlock
        text={`connect(mapStateToProps, mapDispatchToProps, mergeProps, options)(Component)`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <code className={styles.code}>mapStateToProps(state, [ownProps])</code> - функция соединяющая часть состояния с
        пропами компонента. Таким образом, связанный компонент будет иметь доступ к необходимой ему части состояния.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Получает <code className={styles.code}>state</code> как параметр и позволяет выбрать из всего{' '}
          <code className={styles.code}>state</code> только необходимые компоненту слайсы (части).
        </li>
        <li className={styles.list__item}>
          Возвращает объект, свойства которого дополнят <code className={styles.code}>props</code> компонента.
        </li>
        <li className={styles.list__item}>Вызывается каждый раз когда хранилище обновляется.</li>
        <li className={styles.list__item}>
          Если нет необходимости подписываться на обновления, передаем <code className={styles.code}>null</code>.
        </li>
        <li className={styles.list__item}>
          Если функция объявлена ​​как принимающая два параметра, первым будет передана ссылка на{' '}
          <code className={styles.code}>state</code>, а вторым ссылка на пропы самого компонента.
        </li>
      </ul>
      <CopyBlock
        text={`const mapStateToProps = (state, props) => ({
  notes: state.notes,
  currentFilter: state.filter,
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <code className={styles.code}>mapDispatchToProps(dispatch, [ownProps])</code> - функция соединяющая отправку
        действий с пропами компонента. Таким образом, связанный компонент сможет отправлять действия посредством вызова
        методов указанных в возвращаемом объекте.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Получает ссылку на метод <code className={styles.code}>dispatch</code> как параметр и позволяет объявить
          методы для отправки действий.
        </li>
        <li className={styles.list__item}>
          Возвращает объект, свойства которого дополнят <code className={styles.code}>props</code> компонента.
        </li>
        <li className={styles.list__item}>
          Если функция объявлена ​​как принимающая два параметра, первым будет передана ссылка на{' '}
          <code className={styles.code}>dispatch</code>, а вторым ссылка на пропы самого компонента.
        </li>
      </ul>
      <CopyBlock
        text={`const addNote = text => ({
  type: 'ADD_NOTE',
  payload: { text },
});

const mapDispatchToProps = dispatch => ({
  addNote: text => dispatch(addNote(text)),
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Если аргументы действия совпадают с параметрами объявляемого метода, можно вместо функции передать объект. В
        таком случае <code className={styles.code}>connect</code> пройдет по ключам объекта и обернет их в{' '}
        <code className={styles.code}>dispatch</code>.
      </p>
      <CopyBlock
        text={`const addNote = text => ({
  type: 'ADD_NOTE',
  payload: { text },
});

const mapDispatchToProps = { addNote };
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://react-redux.js.org/api/connect"
        >
          Документация connect{' '}
        </a>
      </p>
    </section>
  </section>
))
